/**
 * React binding for resolveActiveProgram — re-renders when the route OR the
 * session program changes (Home picker / "Choose a different program").
 *
 * Single-program bypass (owner 2026-07-21, matching the ProgramLauncher rule
 * from 2026-07-12): when route + session resolve to 'neutral' but the user
 * holds exactly ONE program, that program IS the active context — a
 * single-program user never sees the "Programs" launcher group in the sidebar
 * or program-ambiguous states anywhere else.
 */
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  HOME_PROGRAM_EVENT, getHomeProgram, resolveActiveProgram, type ProgramContext,
} from './activeProgram';
import { useProgramAccess } from './ProgramAccessContext';

export function useActiveProgram(): ProgramContext {
  const { pathname } = useLocation();
  const { grantedModules } = useProgramAccess();
  const [sessionProgram, setSessionProgram] = useState<string | null>(() => getHomeProgram());

  useEffect(() => {
    const sync = () => setSessionProgram(getHomeProgram());
    sync();
    window.addEventListener(HOME_PROGRAM_EVENT, sync);
    return () => window.removeEventListener(HOME_PROGRAM_EVENT, sync);
  }, [pathname]);

  const resolved = resolveActiveProgram(pathname, sessionProgram);
  if (resolved === 'neutral' && grantedModules.length === 1) {
    return grantedModules[0].code as ProgramContext;
  }
  return resolved;
}
